import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { UseQueryResult } from "@tanstack/react-query";
import { Search, Snowflake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useLocalization } from "@/context/localization-context";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { EmptyBlock, FetchingDot, SectionCard } from "../../components/primitives";
import { formatClock, formatCount, formatStamp } from "../../format";
import type { HttpRequestEntry, LiveSection } from "../../types";
import { Duration, LongPollHint, MethodChip, QueryBody, StatusChip } from "./bits";
import { filterTail, isSlowByDesign, shortId, TAIL_FILTERS, TAIL_SIZE, type TailFilter } from "./utils";

interface Frozen {
  entries: HttpRequestEntry[];
  at: number;
}

/** requests of the live list that arrived after the frozen snapshot */
function countNewer(current: HttpRequestEntry[], frozen: Frozen): number {
  const newest = frozen.entries[0]?.ts ?? frozen.at;
  return current.filter((entry) => entry.ts > newest).length;
}

/** colour of a filter's count: 5xx red, 4xx amber, "errors" after the worst class it holds */
function countClass(key: TailFilter, counts: Record<TailFilter, number>): string | undefined {
  if ((key === "s5" || key === "errors") && counts.s5 > 0) return "text-red-300";
  if ((key === "s4" || key === "errors") && counts.s4 > 0) return "text-amber-300";
  return undefined;
}

/** The last requests answered by this instance, newest first, with filters and a freeze switch. */
export function RequestTail({ live }: { live: UseQueryResult<LiveSection> }) {
  const { t } = useLocalization();
  const [filter, setFilter] = useState<TailFilter>("all");
  const [text, setText] = useState("");
  const [frozen, setFrozen] = useState<Frozen | null>(null);

  const current = live.data?.http.recent;
  const entries = useMemo(() => frozen?.entries ?? current ?? [], [frozen, current]);
  const shown = useMemo(() => filterTail(entries, filter, text), [entries, filter, text]);
  const counts = useMemo(
    () => ({
      all: entries.length,
      errors: entries.filter((e) => e.status >= 400).length,
      s4: entries.filter((e) => e.status >= 400 && e.status < 500).length,
      s5: entries.filter((e) => e.status >= 500).length,
    }),
    [entries],
  );
  const newer = frozen && current ? countNewer(current, frozen) : 0;

  return (
    <SectionCard
      title={t("monitoring.requests.tail.title")}
      description={t("monitoring.requests.tail.description", { count: TAIL_SIZE })}
      actions={<FetchingDot active={live.isFetching && !frozen} />}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={filter}
          onValueChange={(value) => value && setFilter(value as TailFilter)}
          aria-label={t("monitoring.requests.tail.filter")}
          className="flex-wrap justify-start gap-0.5"
        >
          {TAIL_FILTERS.map((key) => (
            <ToggleGroupItem key={key} value={key} className="h-8 gap-1.5 px-2 text-xs">
              {t(`monitoring.requests.tail.filter.${key}`)}
              <span className={cn("font-mono tabular-nums text-muted-foreground", countClass(key, counts))}>
                {formatCount(counts[key])}
              </span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <div className="relative min-w-[12rem] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            type="search"
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={t("monitoring.requests.tail.search")}
            aria-label={t("monitoring.requests.tail.search")}
            className="h-8 pl-8 text-xs md:text-xs"
          />
        </div>
        <Button
          variant={frozen ? "secondary" : "outline"}
          size="sm"
          className="h-8 gap-1.5 px-2.5 text-xs"
          aria-pressed={!!frozen}
          disabled={!current && !frozen}
          onClick={() => setFrozen(frozen ? null : { entries: current ?? [], at: Date.now() })}
          title={t("monitoring.requests.tail.freezeHint")}
        >
          <Snowflake className="h-3.5 w-3.5" aria-hidden />
          {frozen ? t("monitoring.requests.tail.frozen") : t("monitoring.requests.tail.freeze")}
        </Button>
      </div>

      {frozen && (
        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground" role="status">
          <span>
            {t("monitoring.requests.tail.frozenAt", { time: formatClock(frozen.at) })}
            {" · "}
            {newer > 0 && newer >= (current?.length ?? 0)
              ? t("monitoring.requests.tail.newerMany", { count: formatCount(newer) })
              : t("monitoring.requests.tail.newer", { count: formatCount(newer) })}
          </span>
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setFrozen(null)}>
            {t("monitoring.requests.tail.showLatest")}
          </Button>
        </div>
      )}

      <QueryBody query={live} height={320}>
        {() => {
          if (!entries.length) return <EmptyBlock>{t("monitoring.requests.tail.empty")}</EmptyBlock>;
          if (!shown.length) return <EmptyBlock>{t("monitoring.requests.tail.noMatch")}</EmptyBlock>;
          return <TailRows entries={shown} />;
        }}
      </QueryBody>
    </SectionCard>
  );
}

function DeviceLink({ device, short = true }: { device: string; short?: boolean }) {
  const { t } = useLocalization();
  return (
    <Link
      to={`/cameras/${encodeURIComponent(device)}`}
      title={device}
      aria-label={t("monitoring.requests.tail.openCamera", { device })}
      className="break-all font-mono underline-offset-2 hover:text-foreground hover:underline"
    >
      {short ? shortId(device) : device}
    </Link>
  );
}

const rowKey = (entry: HttpRequestEntry, index: number) => `${entry.ts}-${entry.method}-${entry.path}-${entry.ms}-${index}`;

function TailRows({ entries }: { entries: HttpRequestEntry[] }) {
  const { t } = useLocalization();
  // one variant in the DOM (the CSS breakpoint classes cover the first frame)
  const mobile = useIsMobile();
  return (
    <>
      {/* md+: table */}
      {!mobile && (
        <div className="hidden md:block">
          <Table className="text-xs" containerClassName="max-h-[36rem] rounded-md border">
            <TableHeader className="sticky top-0 z-10 bg-card">
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-8 w-[5.5rem] px-2">{t("monitoring.col.time")}</TableHead>
                <TableHead className="h-8 w-16 px-2">{t("monitoring.requests.col.method")}</TableHead>
                <TableHead className="h-8 px-2">{t("monitoring.requests.col.path")}</TableHead>
                <TableHead className="h-8 w-14 px-2">{t("monitoring.requests.col.status")}</TableHead>
                <TableHead className="h-8 w-20 px-2 text-right">{t("monitoring.requests.col.duration")}</TableHead>
                <TableHead className="hidden h-8 px-2 lg:table-cell">{t("monitoring.requests.col.client")}</TableHead>
                <TableHead className="hidden h-8 px-2 lg:table-cell">{t("monitoring.requests.col.device")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry, index) => (
                <TableRow key={rowKey(entry, index)} className={cn(entry.status >= 500 && "bg-red-500/[0.06]")}>
                  <TableCell className="whitespace-nowrap px-2 py-1 font-mono tabular-nums text-muted-foreground">
                    <time dateTime={new Date(entry.ts).toISOString()} title={formatStamp(entry.ts)}>
                      {formatClock(entry.ts)}
                    </time>
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    <MethodChip method={entry.method} />
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="min-w-0 break-all font-mono text-[11px]" title={entry.route}>{entry.path}</span>
                      {isSlowByDesign(entry.route) && <LongPollHint />}
                    </div>
                    {(entry.ip || entry.device) && (
                      <div className="mt-0.5 flex flex-wrap gap-x-3 text-[10px] text-muted-foreground lg:hidden">
                        {entry.ip && <span className="font-mono">{entry.ip}</span>}
                        {entry.device && <DeviceLink device={entry.device} />}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    <StatusChip status={entry.status} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap px-2 py-1 text-right">
                    <Duration ms={entry.ms} route={entry.route} />
                  </TableCell>
                  <TableCell className="hidden px-2 py-1 font-mono text-[11px] text-muted-foreground lg:table-cell">
                    <span className="break-all">{entry.ip ?? "–"}</span>
                  </TableCell>
                  <TableCell className="hidden px-2 py-1 text-[11px] text-muted-foreground lg:table-cell">
                    {entry.device ? <DeviceLink device={entry.device} /> : "–"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* below md: cards */}
      {mobile && (
        <ul className="flex max-h-[70vh] flex-col gap-1.5 overflow-y-auto md:hidden">
          {entries.map((entry, index) => (
            <li
              key={rowKey(entry, index)}
              className={cn("rounded-md border px-3 py-2", entry.status >= 500 && "border-red-500/30 bg-red-500/[0.06]")}
            >
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <time
                  dateTime={new Date(entry.ts).toISOString()}
                  title={formatStamp(entry.ts)}
                  className="font-mono text-[11px] tabular-nums text-muted-foreground"
                >
                  {formatClock(entry.ts)}
                </time>
                <MethodChip method={entry.method} />
                <StatusChip status={entry.status} />
                <Duration ms={entry.ms} route={entry.route} className="ml-auto text-xs" />
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <span className="min-w-0 break-all font-mono text-[11px]" title={entry.route}>{entry.path}</span>
                {isSlowByDesign(entry.route) && <LongPollHint />}
              </div>
              {(entry.ip || entry.device) && (
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                  {entry.ip && <span className="break-all font-mono">{entry.ip}</span>}
                  {entry.device && <DeviceLink device={entry.device} short={false} />}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
